import React, { useCallback } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Row, Dialog } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { entities } from "byzantine";
import { CardOffersActivationModalProps } from "./types";
import styles from "./ActivationModal.module.scss";

const TermsLink: React.FC<React.PropsWithChildren> = ({ children }) => {
  const institution = entities.useInstitution();
  const tosUrl =
    institution?.card_linked_offers_enrollment_terms_of_service || "";

  const handleLinkClick = useCallback(() => {
    if (tosUrl) {
      window.open(tosUrl, "_blank", "noopener,noreferrer");
    }
  }, [tosUrl]);

  if (!tosUrl) {
    return <span>{children}</span>;
  }

  return (
    <span
      className={styles.termsLink}
      onClick={handleLinkClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleLinkClick();
        }
      }}
    >
      {children}
    </span>
  );
};

const CardLinkedOffersActivationModal = ({
  isOpen,
  onClose,
  onSuccess,
  testId,
}: CardOffersActivationModalProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { createEnrollment, loading: isEnrolling } =
    entities.cardLinkedOffers.useCreateCardLinkedOffersEnrollment();

  const handleActivate = useCallback(() => {
    createEnrollment({
      onSuccess() {
        sendNotification({
          type: "success",
          text: l10n.getString(
            "card-linked-offers-enroll-success",
            null,
            "Card offers activated."
          ),
        });

        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
        }
      },
      onError() {
        sendNotification({
          type: "negative",
          text: l10n.getString(
            "card-linked-offers-enroll-error",
            null,
            "Unable to activate card offers. Please try again later."
          ),
        });
      },
    });
  }, [createEnrollment, onClose, onSuccess, sendNotification, l10n]);

  return (
    <Dialog
      isOpen={isOpen}
      onUserDismiss={onClose}
      title={l10n.getString(
        "card-offers-modal-title",
        null,
        "Ready to make banking more rewarding?"
      )}
      testId={testId}
      footer={
        <Row justifyContent="end" alignItems="center">
          <Row.Item shrink>
            <Button
              kind="primary"
              isLoading={isEnrolling}
              label={l10n.getString(
                "card-offers-modal-activate-button",
                null,
                "Activate cash back rewards"
              )}
              onClick={handleActivate}
              testId="activate-button"
            />
          </Row.Item>
        </Row>
      }
    >
      <div className={styles.content}>
        {/* Card Icon Section */}
        <div className={styles.cardIconSection}>
          <div className={styles.cardIcon}>
            <span className="narmi-icon-credit-card" />
          </div>
        </div>

        {/* How Cash Back Works Section */}
        <div className={styles.howItWorksSection}>
          <p className={styles.sectionTitle}>
            {l10n.getString(
              "card-offers-modal-how-cash-back-works",
              null,
              "HOW CASH BACK WORKS"
            )}
          </p>

          <div className={styles.stepsContainer}>
            <div className={styles.step} data-testid="step-1">
              <div
                className={`fontSize--s fontWeight--semiBold ${styles.stepNumber}`}
              >
                1
              </div>
              <div className={styles.stepContent}>
                <div className={styles.stepText}>
                  {l10n.getElement(
                    <div className="fontSize--m fontColor--black" />,
                    "card-offers-modal-step-1",
                    {
                      elems: { boldedText: <strong /> },
                    }
                  )}
                </div>
              </div>
            </div>

            <div className={styles.step} data-testid="step-2">
              <div
                className={`fontSize--s fontWeight--semiBold ${styles.stepNumber}`}
              >
                2
              </div>
              <div className={styles.stepContent}>
                <div className={styles.stepText}>
                  {l10n.getElement(
                    <div className="fontSize--m fontColor--black" />,
                    "card-offers-modal-step-2",
                    {
                      elems: { boldedText: <strong /> },
                    }
                  )}
                </div>
              </div>
            </div>

            <div className={styles.step} data-testid="step-3">
              <div
                className={`fontSize--s fontWeight--semiBold ${styles.stepNumber}`}
              >
                3
              </div>
              <div className={styles.stepContent}>
                <div className={styles.stepText}>
                  {l10n.getElement(
                    <div className="fontSize--m fontColor--black" />,
                    "card-offers-modal-step-3",
                    {
                      elems: { boldedText: <strong /> },
                    }
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Terms Notice */}
        <div className={styles.termsNotice}>
          {l10n.getElement(<div />, "card-offers-modal-terms-notice", {
            elems: { termsLink: <TermsLink /> },
          })}
        </div>
      </div>
    </Dialog>
  );
};

export default CardLinkedOffersActivationModal;
